import { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle, Users, TrendingUp, GraduationCap, DollarSign, Camera, Loader2, User, Phone, Calendar } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  { icon: DollarSign, title: 'Competitive Splits', description: 'Industry-leading commission structures that reward your hard work.' },
  { icon: GraduationCap, title: 'Training & Support', description: 'Comprehensive training programs and ongoing mentorship from day one.' },
  { icon: TrendingUp, title: 'Lead Generation', description: 'Access to quality leads and full marketing support to grow your business.' },
  { icon: Users, title: 'Collaborative Culture', description: 'Join a supportive team that helps you grow and succeed together.' }
];

const requirements = [
  'Active Florida Real Estate License (or in process)',
  'Strong work ethic and self-motivation',
  'Excellent communication skills',
  'Commitment to client service',
  'Professional appearance and demeanor',
  'Willingness to learn and grow'
];

export const JoinTeam = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    license_number: '',
    years_experience: '',
    current_brokerage: '',
    bio: '',
    why_join: ''
  });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }

    setPhotoUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPhotoUrl(res.data.url);
      toast.success('Photo uploaded');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, {
        ...formData,
        photo_url: photoUrl,
        years_experience: parseInt(formData.years_experience) || 0
      });
      setSubmitted(true);
      toast.success("Application submitted! We'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="join-team-page">
      {/* Hero Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="join-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="section-overline">Careers at Simple Realty</p>
            <h1 className="text-4xl sm:text-5xl font-light text-[#F5F5F0] mt-4 mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Join Our <span className="text-gold-gradient">Team</span>
            </h1>
            <p className="text-lg text-[#808080] max-w-2xl mx-auto mb-8">
              Build your real estate career with a brokerage that invests in your success. We provide the tools, training, and support you need to thrive.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="tel:7863560878"><Button variant="outline" className="btn-outline-gold"><Phone className="w-4 h-4 mr-2" /> Call to Discuss</Button></a>
              <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer"><Button className="btn-gold"><Calendar className="w-4 h-4 mr-2" /> Schedule Interview</Button></a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-[#111111]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="section-overline">Why Join Us</p>
            <h2 className="section-title mt-2">What We Offer</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="card-hover bg-[#0a0a0a] p-6 rounded-lg text-center">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-light text-[#F5F5F0] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                <p className="text-[#808080] text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="application-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Requirements */}
            <div>
              <p className="section-overline">Requirements</p>
              <h2 className="section-title mt-2 mb-8">What We're Looking For</h2>
              <div className="space-y-4">
                {requirements.map((req) => (
                  <div key={req} className="flex items-start text-[#F5F5F0]">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 mt-0.5 flex-shrink-0" />
                    <span>{req}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-6 bg-[#111111] border border-[#D4AF37]/20 rounded-lg">
                <h4 className="text-[#F5F5F0] font-semibold mb-2">New to Real Estate?</h4>
                <p className="text-[#808080] text-sm">
                  We welcome newly licensed agents! Our training program will help you build a successful career from day one.
                </p>
              </div>
            </div>

            {/* Application Form */}
            <div className="form-section" data-testid="agent-application-form">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
                  <h3 className="text-2xl font-light text-[#F5F5F0] mb-4" style={{ fontFamily: 'Playfair Display' }}>Application Received!</h3>
                  <p className="text-[#808080]">Thank you for your interest. We'll review your application and get back to you within 48 hours.</p>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-light text-[#F5F5F0] mb-2" style={{ fontFamily: 'Playfair Display' }}>Apply Now</h3>
                  <p className="text-[#808080] text-sm mb-6">Fill out the form below and upload your photo to start your journey with SRI.</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Photo Upload */}
                    <div className="flex justify-center mb-2">
                      <div className="relative group cursor-pointer" onClick={() => photoRef.current?.click()} data-testid="application-photo-upload">
                        <input type="file" ref={photoRef} onChange={handlePhotoUpload} accept="image/*" className="hidden" data-testid="application-photo-input" />
                        {photoUrl ? (
                          <img src={photoUrl} alt="Profile" className="w-24 h-24 rounded-full object-cover border-2 border-[#D4AF37]/40" />
                        ) : (
                          <div className="w-24 h-24 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border-2 border-dashed border-[#D4AF37]/30">
                            <User className="w-10 h-10 text-[#D4AF37]/50" />
                          </div>
                        )}
                        <div className="absolute inset-0 w-24 h-24 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          {photoUploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
                        </div>
                      </div>
                    </div>
                    <p className="text-[#808080] text-xs text-center">Click to upload your photo</p>

                    <Input type="text" placeholder="Full Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]" data-testid="application-name-input" />
                    <Input type="email" placeholder="Email Address *" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]" data-testid="application-email-input" />
                    <Input type="tel" placeholder="Phone Number *" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]" data-testid="application-phone-input" />
                    <Input type="text" placeholder="FL License Number (if applicable)" value={formData.license_number} onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]" data-testid="application-license-input" />
                    <Input type="number" placeholder="Years of Experience" value={formData.years_experience} onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })} min="0" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]" data-testid="application-experience-input" />
                    <Input type="text" placeholder="Current Brokerage (if any)" value={formData.current_brokerage} onChange={(e) => setFormData({ ...formData, current_brokerage: e.target.value })} className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]" data-testid="application-brokerage-input" />
                    <Textarea placeholder="Your Bio — Tell us about yourself, your experience, specialties..." value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} rows={3} className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080] resize-none" data-testid="application-bio-input" />
                    <Textarea placeholder="Why do you want to join Simple Realty & Investments™? *" value={formData.why_join} onChange={(e) => setFormData({ ...formData, why_join: e.target.value })} required rows={3} className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080] resize-none" data-testid="application-why-input" />
                    <Button type="submit" className="btn-gold w-full font-medium" disabled={loading} data-testid="application-submit-btn">
                      {loading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
